import { Alert, Dialog, Skeleton, Stack, TextField } from '@all-manager/design-system-v2';
import { Controller } from "react-hook-form";
import DateField from "../../../../../components/modals/components/DateFieldOk";
import DescriptionFieldOk from "../../../../../components/modals/components/DescriptionFieldOk";
import ProviderAutocomplete from "../../../../../components/ProviderAutocomplete/ProviderAutocomplete";
import type { SubjectHistoryFeed } from "../../../../../types";
import DocumentsFormSection from "../../../../documents/external-components/DocumentsFormSection/DocumentsFormSection";
import RevealFieldButton from "../RevealFieldButton/RevealFieldButton";
import useAddOtherActionModal from "./useAddOtherActionModal";

type Props = {
  onClose: () => void;
  subjectId: string;
  item?: SubjectHistoryFeed;
}

export default function AddOtherActionModal(props: Props) {
  const {
    form,
    handleConfirm,
    isLoading,
    serverError,
    isDocumentLoading,
    showComment,
    showProvider,
    showContact,
    showDocuments,
    revealComment,
    revealProvider,
    revealContact,
    revealDocuments,
  } = useAddOtherActionModal(props);

  return (
    <Dialog
      open
      onClose={props.onClose}
      maxWidth="sm"
      fullWidth
      title={props.item ? "Modifier l'action" : "Nouvelle action"}
      closeLabel="Fermer"
      actions={{
        primaryAction: {
          label: 'Enregistrer',
          onClick: form.handleSubmit(handleConfirm),
          loading: isLoading,
          disabled: isDocumentLoading,
        },
        secondaryAction: {
          label: 'Annuler',
          onClick: props.onClose,
          disabled: isLoading,
        },
      }}
    >
      <Stack spacing={2}>
        {serverError ? <Alert severity="error">{serverError}</Alert> : null}

        <DateField
          name="date"
          form={form}
          label="Date"
          isTimeManaged={false}
          required
        />

        <Controller
          control={form.control}
          name='title'
          render={({ field, fieldState }) => (
            <TextField
              name={field.name}
              inputRef={field.ref}
              onBlur={field.onBlur}
              label="Titre"
              fullWidth
              value={field.value ?? ''}
              onChange={(event) => field.onChange(event.target.value)}
              helperText={fieldState.error?.message}
              error={!!fieldState.error}
              required
              autoFocus
            />
          )}
        />

        {showComment ? (
          <DescriptionFieldOk
            form={form}
            name="comment"
            label="Commentaire"
          />
        ) : (
          <RevealFieldButton label="Ajouter un commentaire" onClick={revealComment} />
        )}

        {showProvider ? (
          <Controller
            control={form.control}
            name='provider_name'
            render={({ field, fieldState }) => (
              <ProviderAutocomplete
                label="Prestataire"
                value={field.value ?? ''}
                onChange={field.onChange}
                fullWidth
                error={!!fieldState.error}
                helperText={fieldState.error?.message}
              />
            )}
          />
        ) : (
          <RevealFieldButton label="Ajouter un prestataire" onClick={revealProvider} />
        )}

        {showContact ? (
          <Controller
            control={form.control}
            name='contact_name'
            render={({ field, fieldState }) => (
              <TextField
                name={field.name}
                inputRef={field.ref}
                onBlur={field.onBlur}
                label="Membre du conseil syndical"
                fullWidth
                value={field.value ?? ''}
                onChange={(event) => field.onChange(event.target.value)}
                helperText={fieldState.error?.message}
                error={!!fieldState.error}
              />
            )}
          />
        ) : (
          <RevealFieldButton label="Ajouter un membre du conseil syndical" onClick={revealContact} />
        )}

        {showDocuments ? (
          isDocumentLoading ? (
            <Skeleton variant="rounded" height={120} aria-label="Chargement des documents" />
          ) : (
            <Controller
              name='documents'
              control={form.control}
              render={({ field }) => (
                <DocumentsFormSection
                  documents={field.value}
                  onChange={field.onChange}
                  subjectId={props.subjectId}
                />
              )}
            />
          )
        ) : (
          <RevealFieldButton label="Ajouter des documents" onClick={revealDocuments} />
        )}
      </Stack>
    </Dialog>
  );
}
